import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { customerApi } from "@/api/customer";
import type { Customer } from "@/types/customer";
import { formatPhoneNumber } from "@/lib/utils";

interface CustomerEditDialogProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function CustomerEditDialog({
  customer,
  isOpen,
  onClose,
}: CustomerEditDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [bookingBlocked, setBookingBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  useEffect(() => {
    if (customer && isOpen) {
      setName(customer.name);
      setPhone(customer.phone || "");
      setGender(customer.gender || "");
      setBookingBlocked(customer.bookingBlocked || false);
      setBlockReason(customer.blockReason || "");
    }
  }, [customer, isOpen]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!customer) return;
      return await customerApi.updateCustomer(customer.id, {
        name,
        phone,
        gender: gender || undefined,
        bookingBlocked,
        blockReason: bookingBlocked ? blockReason : undefined,
      });
    },
    onSuccess: () => {
      toast({
        title: "고객 정보 수정 완료",
        description: "고객 정보가 수정되었습니다.",
      });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["consults"] });
      onClose();
    },
    onError: (error: unknown) => {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      toast({
        title: "수정 실패",
        description:
          axiosError.response?.data?.message ||
          "고객 정보 수정에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!name || !phone) {
      toast({
        title: "입력 오류",
        description: "이름과 전화번호는 필수입니다.",
        variant: "destructive",
      });
      return;
    }
    updateMutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>고객 정보 수정</DialogTitle>
          <DialogDescription className="sr-only">고객 정보 수정</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">이름</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="고객명"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">전화번호</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
              placeholder="010-0000-0000"
              maxLength={14}
            />
          </div>
          <div className="grid gap-2">
            <Label>성별</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger>
                <SelectValue placeholder="선택 안함" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MALE">남성</SelectItem>
                <SelectItem value="FEMALE">여성</SelectItem>
                <SelectItem value="OTHER">기타</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 예약 차단 */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label>예약 차단</Label>
                <p className="text-xs text-muted-foreground">
                  이 고객의 예약을 차단합니다
                </p>
              </div>
              <Switch
                checked={bookingBlocked}
                onCheckedChange={setBookingBlocked}
              />
            </div>
            {bookingBlocked && (
              <div className="grid gap-2">
                <Label htmlFor="blockReason">차단 사유</Label>
                <Input
                  id="blockReason"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="차단 사유를 입력하세요"
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "저장 중..." : "저장"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
